import * as THREE from 'three';
import { smoothstep } from '../world/noise';

export type BonePositions = Record<string, THREE.Vector3>;
export type BoneIndex = (name: string) => number;
type Influence = [number, number];

type Ring = {
  c: THREE.Vector3;
  u: THREE.Vector3;
  v: THREE.Vector3;
  ru: number;
  rv: number;
  /** Radial multiplier per angle (pleats, folds). */
  fold?: (theta: number) => number;
};

type SkinFn = (p: THREE.Vector3, ring: number, theta: number) => Influence[];

function normalizeInfluences(list: Influence[]): { idx: number[]; w: number[] } {
  const merged = new Map<number, number>();
  for (const [i, w] of list) if (w > 1e-4) merged.set(i, (merged.get(i) ?? 0) + w);
  const sorted = [...merged.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4);
  const total = sorted.reduce((s, [, w]) => s + w, 0) || 1;
  const idx = [0, 0, 0, 0];
  const w = [0, 0, 0, 0];
  sorted.forEach(([i, weight], k) => {
    idx[k] = i;
    w[k] = weight / total;
  });
  if (sorted.length === 0) w[0] = 1;
  return { idx, w };
}

function buildTube(rings: Ring[], radial: number, skin: SkinFn, flutter?: (ring: number, theta: number) => number): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const skinIndex: number[] = [];
  const skinWeight: number[] = [];
  const flutterAttr: number[] = [];
  const p = new THREE.Vector3();
  rings.forEach((ring, i) => {
    for (let j = 0; j <= radial; j++) {
      const theta = (j / radial) * Math.PI * 2;
      const f = ring.fold ? ring.fold(theta) : 1;
      p.copy(ring.c)
        .addScaledVector(ring.u, Math.cos(theta) * ring.ru * f)
        .addScaledVector(ring.v, Math.sin(theta) * ring.rv * f);
      positions.push(p.x, p.y, p.z);
      uvs.push(j / radial, i / (rings.length - 1));
      const { idx, w } = normalizeInfluences(skin(p, i, theta));
      skinIndex.push(...idx);
      skinWeight.push(...w);
      flutterAttr.push(flutter ? flutter(i, theta) : 0);
    }
  });
  const indices: number[] = [];
  const row = radial + 1;
  for (let i = 0; i < rings.length - 1; i++) {
    for (let j = 0; j < radial; j++) {
      const a = i * row + j;
      const b = a + 1;
      const c = a + row;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndex, 4));
  geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeight, 4));
  geometry.setAttribute('flutter', new THREE.Float32BufferAttribute(flutterAttr, 1));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

const X = new THREE.Vector3(1, 0, 0);
const Y = new THREE.Vector3(0, 1, 0);
const Z = new THREE.Vector3(0, 0, 1);

/** Wide pleated hakama trousers, one flared tube per leg. */
export function buildHakama(bones: BonePositions, bone: BoneIndex): THREE.BufferGeometry {
  const hips = bone('Hips');
  const parts: THREE.BufferGeometry[] = [];
  for (const side of [1, -1]) {
    const pre = side > 0 ? 'Left' : 'Right';
    const upLeg = bone(`${pre}UpLeg`);
    const leg = bone(`${pre}Leg`);
    const hipY = bones.Hips.y;
    const kneeY = bones[`${pre}Leg`].y;
    const legX = bones[`${pre}UpLeg`].x;
    // y, centerX offset (outward), rx, rz, pleat depth
    const keys = [
      [1.1, 0.045, 0.125, 0.16, 0.0],
      [1.0, 0.06, 0.13, 0.16, 0.01],
      [0.88, 0.07, 0.13, 0.155, 0.02],
      [0.72, 0.08, 0.14, 0.165, 0.03],
      [0.55, 0.09, 0.15, 0.18, 0.035],
      [0.38, 0.1, 0.165, 0.195, 0.04],
      [0.22, 0.108, 0.175, 0.205, 0.045],
      [0.1, 0.112, 0.18, 0.21, 0.045],
    ];
    const rings: Ring[] = keys.map((k) => {
      const y = k[0];
      const pleat = k[4];
      return {
        c: new THREE.Vector3(side * k[1], y, 0.012),
        u: X,
        v: Z,
        ru: k[2],
        rv: k[3],
        fold: (theta: number) => 1 + pleat * Math.pow(Math.abs(Math.sin(theta * 4.5)), 0.6) - pleat * 0.4,
      };
    });
    const skin: SkinFn = (p) => {
      const kneeBlend = smoothstep(kneeY + 0.14, kneeY - 0.06, p.y);
      const hipBlend = smoothstep(hipY + 0.03, hipY - 0.22, p.y);
      // Inner-thigh vertices follow their own leg; outer top stays with the pelvis longer.
      const outward = Math.max(0, (p.x - legX) * side);
      const pelvisHold = (1 - hipBlend) + smoothstep(0.06, 0.16, outward) * 0.25 * (1 - kneeBlend);
      const lower = kneeBlend;
      return [
        [hips, pelvisHold],
        [upLeg, (1 - lower) * hipBlend + lower * 0.35],
        [leg, lower * 0.65],
      ];
    };
    parts.push(buildTube(rings, 28, skin, (ring) => smoothstep(3, 7, ring) * 1.0));
  }
  return mergeGeometries(parts);
}

/** Loose kimono top tucked into the hakama, closing around the neck. */
export function buildKimonoTop(bone: BoneIndex): THREE.BufferGeometry {
  const hips = bone('Hips');
  const spine = bone('Spine');
  const spine1 = bone('Spine1');
  const spine2 = bone('Spine2');
  const neck = bone('Neck');
  const lShoulder = bone('LeftShoulder');
  const rShoulder = bone('RightShoulder');
  const lArm = bone('LeftArm');
  const rArm = bone('RightArm');
  // y, rx, rz, z center
  const keys = [
    [0.98, 0.175, 0.15, 0.012],
    [1.06, 0.172, 0.148, 0.015],
    [1.16, 0.168, 0.142, 0.018],
    [1.26, 0.178, 0.148, 0.01],
    [1.35, 0.196, 0.152, -0.006],
    [1.42, 0.2, 0.14, -0.02],
    [1.47, 0.15, 0.11, -0.025],
    [1.51, 0.085, 0.075, -0.025],
  ];
  const rings: Ring[] = keys.map((k) => ({
    c: new THREE.Vector3(0, k[0], k[3]),
    u: X,
    v: Z,
    ru: k[1],
    rv: k[2],
    fold: (theta: number) => 1 + 0.012 * Math.sin(theta * 9 + k[0] * 11),
  }));
  const skin: SkinFn = (p) => {
    const y = p.y;
    const w: Influence[] = [];
    const bands: Array<[number, number, number]> = [
      [hips, 0.98, 1.08],
      [spine, 1.08, 1.2],
      [spine1, 1.2, 1.3],
      [spine2, 1.3, 1.44],
      [neck, 1.44, 1.56],
    ];
    for (let i = 0; i < bands.length; i++) {
      const [b, lo, hi] = bands[i];
      const mid = (lo + hi) / 2;
      const span = (hi - lo) * 0.9;
      const weight = Math.max(0, 1 - Math.abs(y - mid) / span);
      w.push([b, weight]);
    }
    const shoulderZone = smoothstep(1.3, 1.42, y) * smoothstep(0.08, 0.19, Math.abs(p.x));
    if (shoulderZone > 0) {
      const left = p.x > 0;
      w.push([left ? lShoulder : rShoulder, shoulderZone * 0.5]);
      w.push([left ? lArm : rArm, shoulderZone * 0.45]);
    }
    return w;
  };
  return buildTube(rings, 40, skin);
}

/** Wide kimono sleeves around upper arm and forearm. */
export function buildSleeves(bones: BonePositions, bone: BoneIndex): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  for (const side of [1, -1]) {
    const pre = side > 0 ? 'Left' : 'Right';
    const shoulder = bone(`${pre}Shoulder`);
    const arm = bone(`${pre}Arm`);
    const fore = bone(`${pre}ForeArm`);
    const armPos = bones[`${pre}Arm`];
    const elbowX = Math.abs(bones[`${pre}ForeArm`].x);
    // |x|, ry (vertical), rz, y drop
    const keys = [
      [0.11, 0.09, 0.09, 0.0],
      [0.17, 0.105, 0.1, 0.0],
      [0.27, 0.125, 0.11, -0.012],
      [0.37, 0.145, 0.118, -0.025],
      [0.47, 0.16, 0.125, -0.04],
      [0.56, 0.172, 0.128, -0.055],
      [0.63, 0.176, 0.13, -0.062],
    ];
    const rings: Ring[] = keys.map((k) => ({
      c: new THREE.Vector3(side * k[0], armPos.y + k[3], armPos.z),
      u: Y,
      v: Z,
      ru: k[1],
      rv: k[2],
      fold: (theta: number) => 1 + 0.02 * Math.sin(theta * 6 + k[0] * 20),
    }));
    const skin: SkinFn = (p) => {
      const x = Math.abs(p.x);
      const elbow = smoothstep(elbowX - 0.07, elbowX + 0.05, x);
      const shoulderHold = smoothstep(0.2, 0.1, x);
      return [
        [shoulder, shoulderHold * 0.45],
        [arm, (1 - elbow) * (1 - shoulderHold * 0.45)],
        [fore, elbow],
      ];
    };
    const tube = buildTube(rings, 22, skin, (ring) => smoothstep(1, 6, ring) * 1.4);
    // Ring centre and drape weight, so the shader can let a raised sleeve fall toward the shoulder.
    const sleeve: number[] = [];
    rings.forEach((ring, i) => {
      for (let j = 0; j <= 22; j++) sleeve.push(ring.c.x, ring.c.y, ring.c.z, smoothstep(0.5, 5, i));
    });
    tube.setAttribute('aSleeve', new THREE.Float32BufferAttribute(sleeve, 4));
    parts.push(tube);
  }
  return mergeGeometries(parts);
}

/** Cream obi sash wrapped over the waist seam. */
export function buildObi(bone: BoneIndex): THREE.BufferGeometry {
  const hips = bone('Hips');
  const spine = bone('Spine');
  const keys = [
    [0.97, 0.19, 0.165],
    [0.98, 0.2, 0.178],
    [1.075, 0.198, 0.176],
    [1.085, 0.188, 0.166],
  ];
  const rings: Ring[] = keys.map((k) => ({
    c: new THREE.Vector3(0, k[0], 0.012),
    u: X,
    v: Z,
    ru: k[1],
    rv: k[2],
  }));
  return buildTube(rings, 36, () => [
    [hips, 0.6],
    [spine, 0.4],
  ]);
}

/** Cloth wrapped around the neck and lower face (ninja style). */
export function buildNeckWrap(bones: BonePositions, bone: BoneIndex): THREE.BufferGeometry {
  const neck = bone('Neck');
  const head = bone('Head');
  const spine2 = bone('Spine2');
  const n = bones.Neck;
  const keys = [
    [n.y - 0.05, 0.1, 0.09, -0.02],
    [n.y + 0.0, 0.083, 0.08, -0.018],
    [n.y + 0.06, 0.078, 0.082, -0.01],
    [n.y + 0.12, 0.084, 0.092, 0.0],
    [n.y + 0.155, 0.08, 0.09, 0.005],
  ];
  const rings: Ring[] = keys.map((k) => ({
    c: new THREE.Vector3(0, k[0], k[3]),
    u: X,
    v: Z,
    ru: k[1],
    rv: k[2],
    fold: (theta: number) => 1 + 0.03 * Math.sin(theta * 5 + k[0] * 30),
  }));
  return buildTube(rings, 24, (p) => {
    const t = smoothstep(n.y - 0.05, n.y + 0.15, p.y);
    return [
      [spine2, (1 - t) * 0.6],
      [neck, 0.6],
      [head, t * 0.8],
    ];
  });
}

/** Crossed kimono collar (eri): a light band from the back of the neck down to the chest. */
export function buildCollar(bone: BoneIndex): THREE.BufferGeometry {
  const spine2 = bone('Spine2');
  const spine1 = bone('Spine1');
  const neck = bone('Neck');
  const parts: THREE.BufferGeometry[] = [];
  for (const side of [1, -1]) {
    const over = side > 0 ? 0.006 : 0;
    const path = new THREE.CatmullRomCurve3([
      new THREE.Vector3(-side * 0.03, 1.22, 0.163 + over),
      new THREE.Vector3(side * 0.035, 1.3, 0.158 + over),
      new THREE.Vector3(side * 0.07, 1.39, 0.132),
      new THREE.Vector3(side * 0.083, 1.46, 0.07),
      new THREE.Vector3(side * 0.07, 1.51, -0.02),
      new THREE.Vector3(side * 0.02, 1.525, -0.075),
      new THREE.Vector3(-side * 0.02, 1.525, -0.08),
    ]);
    const tube = new THREE.TubeGeometry(path, 36, 0.0095, 6, false);
    tube.scale(1, 1, 1);
    const pos = tube.getAttribute('position');
    const skinIndex: number[] = [];
    const skinWeight: number[] = [];
    const flutter: number[] = [];
    for (let i = 0; i < pos.count; i++) {
      const y = pos.getY(i);
      const t = smoothstep(1.3, 1.46, y);
      const lower = smoothstep(1.3, 1.2, y);
      const { idx, w } = normalizeInfluences([
        [spine1, lower],
        [spine2, (1 - t) * (1 - lower) + 0.2],
        [neck, t],
      ]);
      skinIndex.push(...idx);
      skinWeight.push(...w);
      flutter.push(0);
    }
    tube.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndex, 4));
    tube.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeight, 4));
    tube.setAttribute('flutter', new THREE.Float32BufferAttribute(flutter, 1));
    parts.push(tube);
  }
  return mergeGeometries(parts);
}

export function mergeGeometries(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const merged = new THREE.BufferGeometry();
  const names = ['position', 'normal', 'uv', 'skinIndex', 'skinWeight', 'flutter', 'aSleeve'];
  let vertexOffset = 0;
  const indices: number[] = [];
  const data: Record<string, number[]> = {};
  for (const name of names) data[name] = [];
  for (const part of parts) {
    for (const name of names) {
      const attr = part.getAttribute(name);
      if (attr) data[name].push(...Array.from(attr.array as ArrayLike<number>));
    }
    const index = part.getIndex();
    if (index) for (let i = 0; i < index.count; i++) indices.push(index.getX(i) + vertexOffset);
    vertexOffset += part.getAttribute('position').count;
  }
  merged.setAttribute('position', new THREE.Float32BufferAttribute(data.position, 3));
  merged.setAttribute('normal', new THREE.Float32BufferAttribute(data.normal, 3));
  merged.setAttribute('uv', new THREE.Float32BufferAttribute(data.uv, 2));
  merged.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(data.skinIndex, 4));
  merged.setAttribute('skinWeight', new THREE.Float32BufferAttribute(data.skinWeight, 4));
  merged.setAttribute('flutter', new THREE.Float32BufferAttribute(data.flutter, 1));
  if (data.aSleeve.length) merged.setAttribute('aSleeve', new THREE.Float32BufferAttribute(data.aSleeve, 4));
  merged.setIndex(indices);
  return merged;
}
